import { makeObservable, observable, action } from "mobx";
import axios from "axios";
import is from "is";
import { decompressJson, getIdFromCaption } from "../utils/helper";
import { sendHostedTableRequest } from "../utils/helperTables";
import { nanoid } from "nanoid";
import GC from "@mescius/spread-sheets";

GC.Spread.Sheets.LicenseKey = process.env.REACT_APP_SPREADJS18_KEY;

export class StoreFiles {
  filesList = observable([]);
  hostedFilesList = observable([]);
  isLoaded = false;
  isWorking = false;
  isWorkingElement = null;
  indexedDb = null;
  activeFileModified = false;
  activeSpreadSheet = null;
  activeFileInfo = null;
  activeFileName = null;
  activeFileId = null;
  activeFile = null;
  activeFileRef = null;
  afterFileLoadInfo = null;
  createdHostedFileId = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.filesList = [];
    this.hostedFilesList = [];
    this.lastUpdate = new Date();

    makeObservable(this, {
      isLoaded: observable,
      isWorking: observable,
      isWorkingElement: observable,
      setIsLoaded: action,
      setIsWorking: action,
      setIsWorkingElement: action,
      filesList: observable,
      hostedFilesList: observable,
      setHostedFilesList: action,
      activeFileModified: observable,
      setActiveFileModified: action,
      setActiveSpreadSheet: action,
      setActiveFileId: action,
      setActiveFileInfo: action,
    });
  }

  // ==================================
  // INDEXEDDB
  // ==================================

  async initializeIndexDb() {
    if (this.indexedDb) return;

    let { Dexie } = await import("dexie");
    const db = new Dexie("airrange-db");
    db.version(1).stores({
      fileHandles: "++id, fileId, ref, name, createdAt",
    });
    this.indexedDb = db;
  }

  async isFileHandleAvailable(fileId) {
    if (!fileId) return false;
    await this.initializeIndexDb();

    let result = false;
    try {
      const tableExists = this.indexedDb["fileHandles"];
      if (!tableExists) return false;

      const count = await this.indexedDb.fileHandles
        .where({ fileId: fileId })
        .count();
      result = count > 0;
    } catch (error) {
      console.log("ERROR isFileHandleAvailable", error);
    }
    return result;
  }

  async getFileHandle(fileId) {
    if (!fileId) return;
    await this.initializeIndexDb();

    try {
      const tableExists = this.indexedDb["fileHandles"];
      if (!tableExists) return;

      let fileHandles =
        (await this.indexedDb.fileHandles
          .where({ fileId: fileId })
          .toArray()) ?? [];
      if (fileHandles.length === 0) return;

      // get the last handle
      fileHandles = fileHandles.sort((a, b) => {
        return b?.createdAt - a?.createdAt;
      });

      const fileHandle = fileHandles[0].ref;
      const fileHandleId = fileHandles[0].id;

      // delete the old file handles
      if (fileHandles.length > 1) {
        fileHandles.forEach((element, index) => {
          let id = element.id;
          if (id !== fileHandleId) {
            this.indexedDb.fileHandles.delete(id).then(function (deleteCount) {
              console.log("Deleted old fileId: " + id);
            });
          }
        });
      }
      return fileHandle;
    } catch (error) {
      console.log("ERROR getFileHandle", error);
      return;
    }
  }

  async storeFileHandle(fileId, fileRef) {
    if (!fileRef || !fileId) return;
    await this.initializeIndexDb();

    try {
      // try to delete the existing file record
      const tableExists = this.indexedDb["fileHandles"];
      if (tableExists === true) {
        this.indexedDb.fileHandles
          .where("fileId")
          .anyOf(fileId, "")
          .delete()
          .then(function (deleteCount) {
            console.log("Deleted " + deleteCount + " existing objects");
          })
          .catch(function (error) {
            console.log("ERROR delete fileHandle", error);
          });
      }

      const id = await this.indexedDb.fileHandles.add({
        ref: fileRef,
        fileId: fileId,
        name: fileRef.name,
        createdAt: new Date(),
      });
      console.log("INFO: stored local file handle", id);
    } catch (error) {
      console.log("ERROR storeFileHandle", error);
    }
    // clear the handle
    fileRef = null;
  }

  setActiveFileHandle(file, fileRef) {
    this.activeFile = file;
    this.activeFileRef = fileRef;
  }

  setCreatedHostedFileId(fileId) {
    this.createdHostedFileId = fileId;
  }

  setActiveFileName(fileName) {
    this.activeFileName = fileName;
    if (this.activeFileInfo) {
      this.activeFileInfo.fileName = fileName;
    }
    this.activeFileModified = true;
    // update the header caption
    this.rootStore.main.setViewTitle(fileName);
  }

  setHostedFilesList(list) {
    this.hostedFilesList = list;
  }

  // ==================================
  // FUNCTIONS
  // ==================================

  setIsLoaded(loaded) {
    this.isLoaded = loaded;
  }

  setIsWorking(value) {
    this.isWorking = value;
  }

  setIsWorkingElement(element) {
    this.isWorkingElement = element;
  }

  closeFile() {
    console.log("INFO closeFile");
    this.setActiveFileModified(false);
    this.setActiveSpreadSheet(null);
    this.setActiveFileId(null);
    this.setActiveFileInfo(null);
    this.setCreatedHostedFileId(null);
    this.setAfterFileLoadInfo(null);
  }

  setActiveFileModified(modified) {
    this.activeFileModified = modified ? true : false;
  }

  async setActiveSpreadSheet(spreadSheet) {
    this.activeSpreadSheet = spreadSheet;
  }

  async setActiveFileId(fileId) {
    this.activeFileId = fileId;
  }

  async setActiveFileInfo(fileInfo) {
    this.activeFileInfo = fileInfo;
  }

  async setAfterFileLoadInfo(info) {
    this.afterFileLoadInfo = info;
  }

  async saveActiveFile(callback) {
    console.log("INFO saveActiveFile");
    this.setIsWorking(true);
    try {
      let spreadJson = await this.getActiveSpreadSheetJson();
      await this.rootStore.appsync.updateHostedFile(
        this.activeFileInfo,
        spreadJson
      );
      // update the name in recents documents
      if (this.activeFileName) {
        let fileId = this.activeFileId;
        this.rootStore.workbooks.renameRecentsDocument(
          fileId,
          this.activeFileName
        );
      }
      // update the local store if needed
      callback && callback(spreadJson);

      setTimeout(() => {
        this.setActiveFileModified(false);
      }, 100);
    } catch (error) {
      console.log("ERROR saveActiveFile", error);
    } finally {
      this.setIsWorking(false);
    }
  }

  // ==================================
  // SAMPLE FILES
  // ==================================

  async loadSampleData(item) {
    let templateUrl = item?.templateSrc;
    if (!templateUrl) return;
    try {
      let fileData = await this.downloadHostedFileByUrl(templateUrl);
      const buffer = await fileData.text();
      let templateData = JSON.parse(buffer);
      if (!templateData) return;
      let fileId = templateData.fileId;
      let fileJson = templateData.fileJson;
      let fileName = templateData.fileName;
      // let authorName = templateInfo.authorName;
      // let linkedConfigurations = templateInfo.linkedConfigurations;
      // set the new name
      if (fileName) {
        this.setActiveFileName(fileName);
        this.rootStore.main.setViewInfo({
          headerCaption: fileName,
          headerIsVisible: false,
          viewType: "file",
        });
      }
      // update the fileInfo
      let activeFileInfo = this.activeFileInfo;
      let templateInfo = {
        fileId: fileId,
        authorName: templateData.authorName,
        linkedConfigurations: templateData.linkedConfigurations,
      };
      activeFileInfo.templateInfo = templateInfo;
      await this.setActiveFileInfo(activeFileInfo);

      // load the spreadsheet data
      await this.replaceActiveSpread(fileJson);
    } catch (error) {
      console.log("ERROR loadSampleData", error);
    }
  }

  // ==================================
  // HOSTED FILES
  // ==================================

  async getHostedFilesList(refresh = true) {
    if (!refresh && this.hostedFilesList && this.hostedFilesList.length > 0) {
      return this.hostedFilesList;
    }

    let list = await this.rootStore.appsync.getHostedFilesForUser();
    list.forEach((item) => {
      item.fileType = "hostedfile";
      item.type = "workbook";
      item.title = item.fileName ?? "";
      item.parent = null;
      item.topImageUrl = null;
    });
    this.setHostedFilesList(list);
    return list;
  }

  async getWorkbookLinkedTables() {
    let list = [];
    let workbookList = await this.getHostedFilesList(false);
    workbookList.forEach((item) => {
      if (item.linkedTables && item.linkedTables.length > 0) {
        item.linkedTables.forEach((table) => {
          list.push({
            tableId: table,
            workbook: {
              id: item.id,
              title: item.title,
              dataSize: item.dataSize,
            },
          });
        });
      }
    });
    return list;
  }

  async getLinkedWorkbooksForTable(tableId) {
    const workbookLinkedTables = await this.getWorkbookLinkedTables();
    // linked workbooks
    let workbooks = [];
    if (workbookLinkedTables.some((table) => table.tableId === tableId)) {
      const linkedWorkbooks = workbookLinkedTables.filter(
        (table) => table.tableId === tableId
      );
      linkedWorkbooks.forEach((workbookInfo) => {
        workbooks.push(workbookInfo?.workbook);
      });
    }
    return workbooks;
  }

  getNewHostedFileId() {
    const userDomain = this.rootStore.main.userDomain ?? "";
    const domainString = getIdFromCaption(userDomain) ?? "default";
    const newId =
      domainString + "_" + nanoid() + "_" + domainString.length + "_hosted";
    return newId;
  }

  async createNewHostedFile(templateInfo = "") {
    const hostedFileId = this.getNewHostedFileId();
    this.activeNewHostedFileId = hostedFileId;
    // create the hosted file record
    const newId = await this.rootStore.appsync.createHostedFile(
      hostedFileId,
      null,
      templateInfo
    );
    this.setCreatedHostedFileId(newId);
    // open the file in the viewer
    this.rootStore.openUrl("/file/" + hostedFileId);
  }

  async createNewHostedFileFromTable(templateInfo, tableItem) {
    const hostedFileId = this.getNewHostedFileId();
    const tableTitle = tableItem?.title;
    if (!templateInfo) {
      const fileTitle = tableTitle + " - Analytics";
      templateInfo = {
        fileTitle: fileTitle,
      };
    }
    this.activeNewHostedFileId = hostedFileId;
    // create the hosted file record
    const newId = await this.rootStore.appsync.createHostedFile(
      hostedFileId,
      null,
      templateInfo
    );
    this.setCreatedHostedFileId(newId);
    this.setAfterFileLoadInfo({
      tableInfo: tableItem,
      templateInfo: templateInfo,
    });
    // open the file in the viewer
    this.rootStore.openUrl("/file/" + hostedFileId);
  }

  async runAfterFileLoadInfo() {
    const tableInfo = this.afterFileLoadInfo.tableInfo;
    if (tableInfo) {
      await this.addTableSheet(this.activeSpreadSheet, tableInfo, {
        addTableInfos: true,
      });
    }
    // add some basic info

    this.setAfterFileLoadInfo(null);
  }

  async getFileInfoFromFileId(fileId) {
    const isHostedFile = fileId.includes("_hosted");
    let fileInfo;

    if (isHostedFile) {
      // get the hosted file from aws
      fileInfo = await this.rootStore.appsync.getHostedFile(fileId);
      let fileUrl = fileInfo?.fileUrl;
      if (fileInfo?.data) {
        let spreadJson = is.string(fileInfo?.data)
          ? decompressJson(fileInfo?.data)
          : fileInfo?.data;
        return spreadJson;
      }
      // it is a bigger file, download it
      if (fileUrl) {
        fileInfo = await this.downloadHostedFileByUrl(fileUrl);
        return fileInfo;
      }
    } else {
      // load a local file
      if (this.isFileHandleAvailable(fileId)) {
        let fileRef = await this.getFileHandle(fileId);
        const permission = await fileRef.requestPermission();
        if (permission !== "granted") {
          return;
        }
        try {
          const fileObj = await fileRef.getFile();
          // process the file
          let spreadJson =
            await this.rootStore.workbooks.getSpreadJsonFromFileAsync(
              fileObj,
              GC
            );
          return spreadJson;
        } catch (error) {
          console.log("ERROR getFileFromFileHandle", error);
        }
        fileInfo = fileRef;
      }
    }
  }

  async getFileInfoFromHostedFileId(fileId) {
    let fileUrl = null;
    let blobFile = {};
    let fileInfo = null;

    this.setActiveFileId(fileId);
    this.activeNewHostedFileId = null;
    this.activeFileModified = false;

    // get the hosted file from aws
    fileInfo = await this.rootStore.appsync.getHostedFile(fileId);
    fileUrl = fileInfo?.fileUrl; // ?? "https://airrange-content.s3.eu-central-1.amazonaws.com/samples/sample-quotation.xlsx";
    if (fileInfo?.data) {
      let spreadJson = is.string(fileInfo?.data)
        ? decompressJson(fileInfo?.data)
        : fileInfo?.data;
      fileInfo.data = spreadJson;
    }
    // it is a bigger file, download it
    if (fileUrl) {
      this.activeFileModified = true;
      blobFile = await this.downloadHostedFileByUrl(fileUrl);
      fileInfo.data = blobFile;
    }
    let activeFileInfo = {
      fileId: fileInfo?.fileId,
      fileName: fileInfo?.fileName,
      fileType: fileInfo?.fileType,
      templateInfo: fileInfo?.templateInfo,
      topImageUrl: fileInfo?.topImageUrl,
      collaboratorEmails: fileInfo?.collaboratorEmails,
    };
    this.setActiveFileInfo(activeFileInfo);
    return fileInfo;
  }

  async updateActiveFileInfoCollaborators(collaboratorEmails) {
    if (!this.activeFileInfo) return;

    let activeFileInfo = this.activeFileInfo;
    activeFileInfo.collaboratorEmails = collaboratorEmails;
    await this.setActiveFileInfo(activeFileInfo);
    this.setActiveFileModified(true);
    await this.saveActiveFile();
  }

  async getActiveSpreadSheetJson() {
    if (!this.activeSpreadSheet) return;
    let jsonData = this.activeSpreadSheet.toJSON();
    // remove online data from the json
    let tables = jsonData?.dataManager?.tables;
    if (tables) {
      tables.forEach((table) => {
        const dataSourceOption = table.dataSourceOption;
        if (dataSourceOption) {
          dataSourceOption.data = null;
        }
      });
    }
    return jsonData;
  }

  //=========================================
  // IMPORT EXCEL FILE
  //=========================================

  async replaceActiveSpread(spreadJson, options) {
    const spread = this.activeSpreadSheet;
    if (!spread || !spreadJson) return;
    if (!spreadJson?.version) return;

    spread.suspendPaint();
    spread.suspendCalcService(true);
    try {
      // check if we need new worksheets
      await spread.fromJSON(spreadJson, {
        calcOnDemand: false,
        allowDynamicArray: true,
        dynamicReferences: true,
        doNotRecalculateAfterLoad: false,
      });
      this.setActiveFileModified(true);
    } catch (error) {
      console.log("ERROR updateActiveSpread", error);
    } finally {
      spread.resumePaint();
      spread.resumeCalcService(true);
    }
  }

  async exportToExcel(spread, exportFileName, options, worksheetsToRemove) {
    const activeSpread = spread ?? this.activeSpreadSheet;
    if (!activeSpread) return;
    try {
      let workbookJson = await activeSpread.toJSON();
      // load the updated json and export
      const exportBook = new GC.Spread.Sheets.Workbook();
      exportBook.fromJSON(workbookJson);
      if (worksheetsToRemove && worksheetsToRemove.length > 0) {
        worksheetsToRemove.forEach((sheet, index) => {
          const worksheet = exportBook.getSheetFromName(sheet);
          if (worksheet) {
            worksheet.visible(GC.Spread.Sheets.SheetTabVisible.veryHidden);
          }
        });
      }

      const fileName =
        exportFileName ?? this.activeFileInfo?.fileName ?? "spreadsheet";

      exportBook.export(
        function (blob) {
          // save blob to a file
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName + ".xlsx";
          a.click();
        },
        function (e) {
          console.log(e);
        },
        {
          fileType: GC.Spread.Sheets.FileType.excel,
        }
      );
    } catch (error) {
      console.log("ERROR exportToExcel", error);
    }
  }

  async exportToTemplateJSON() {
    if (!this.activeSpreadSheet) return;

    const fileName = this.activeFileInfo?.fileName ?? "spreadsheet";
    const fileId = this.activeFileInfo?.fileId ?? this.activeFileId;
    let fileJson = await this.activeSpreadSheet.toJSON();

    let blob = {
      fileName: fileName,
      fileId: fileId,
      authorName: this.rootStore.main.userEmail,
      linkedConfigurations: [],
      fileJson: fileJson,
    };

    var dataStr =
      "data:text/json;charset=utf-8," +
      encodeURIComponent(JSON.stringify(blob));
    var downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", fileName + ".json");
    document.body.appendChild(downloadAnchorNode); // required for firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  }

  //=========================================
  // UPDATE WORKSHEETS AND CELLS
  //=========================================

  async updateActiveSpread(updateData, options) {
    const spread = this.activeSpreadSheet;
    if (!spread) return;

    let isOK = false;
    spread.suspendPaint();
    spread.suspendCalcService(true);
    try {
      // check if we need new worksheets
      isOK = await this.updateWorksheets(spread, updateData, options);
    } catch (error) {
      console.log("ERROR updateActiveSpread", error);
    } finally {
      spread.resumePaint();
      spread.resumeCalcService(true);
    }
    if (isOK) {
      this.setActiveFileModified(true);
      return true;
    }

    // ERROR
  }

  async updateWorksheets(spread, updateData, options) {
    const sheets = updateData?.sheets;
    if (!sheets) return false;
    let addNewSheet = options?.includes("new-worksheet") ?? false;

    let sheetName;

    for (const [key] of Object.entries(sheets)) {
      let sheet;
      if (addNewSheet) {
        sheet = new GC.Spread.Sheets.Worksheet();
        sheet.zoom(1.0);
        // sheet.name(key);
        spread.addSheet(0, sheet);
      } else {
        sheet = spread.getActiveSheet();
      }
      sheetName = sheet?.name();
      // update the cells of the worksheet
      try {
        sheet && this.updateSheetCellData(sheet, sheets[key]);
      } catch (error) {
        console.log("ERROR updateWorksheets", error);
      }

      // search for the worksheet by name
      // let sheet = spread.getSheetFromName(key);
      // if (!sheet) {
      //   sheetName = key;
      //   sheet = new GC.Spread.Sheets.Worksheet();
      //   sheet.zoom(1.0);
      //   sheet.name(key);
      //   spread.addSheet(0, sheet);
      // }
      // // update the cells of the worksheet
      // try {
      //   this.updateSheetCellData(sheet, sheets[key]);
      // } catch (error) {
      //   console.log("ERROR updateWorksheets", error);
      // }
    }
    if (sheetName) spread.setActiveSheet(sheetName);

    return true;
  }

  async updateSheetCellData(sheet, updateData) {
    let data = updateData?.data?.dataTable;
    const columnList = [];
    if (!data) return;
    for (const [rowKey, rowObject] of Object.entries(data)) {
      // key is the cell row
      const rowIndex = parseInt(rowKey);
      for (const [columnKey, cellObject] of Object.entries(rowObject)) {
        const columnIndex = parseInt(columnKey);
        columnList.push(columnIndex);
        this.updateSheetWriteCellInfo(sheet, rowIndex, columnIndex, cellObject);
      }
    }
    // optimize columns
    columnList.forEach((columnIndex) => {
      sheet.autoFitColumn(columnIndex);
    });
  }

  getFormatterForValue(cellObject) {
    if (!cellObject?.value) return null;
    let format = cellObject.format ?? "";
    if (cellObject.format === "0") format = "0";

    let formatter = format;
    if (is.number(cellObject.value)) {
      if (!formatter) formatter = "0,0";
    }
    if (is.decimal(cellObject.value)) {
      if (!formatter) formatter = "0,0.00";
      // formatter = '[<0](0.0);[>0]0.0;"zero";@'
    }
    if (format) {
      if (format.toLowerCase() === "currency") {
        if (!formatter) formatter = "$0,0.00";
      }
      if (format.toLowerCase() === "percent" || format.includes("%")) {
        if (!formatter) formatter = "0.00%";
      }
    }
    // if (is.date(cellObject.value)) {
    //   formatter = "Short Date";
    // }
    return formatter;
  }

  updateSheetWriteCellInfo(sheet, rowIndex, columnIndex, cellObject) {
    if (!sheet || !cellObject) return;

    const cell = sheet.getCell(rowIndex, columnIndex);
    if (!cell) return;

    // write the value
    cell.value(cellObject.value);

    // formulas
    let formula = cellObject.formula;
    if (formula) cell.formula(formula);
    // formatter
    let formatter = this.getFormatterForValue(cellObject);
    let isBold = false;
    // if (formatter) cell.formatter(formatter);
    // styles
    const cellType = cellObject.type;
    if (cellType) {
      let style = new GC.Spread.Sheets.Style();
      if (cellType === "header") {
        isBold = true;
      }
      if (cellType === "cell-input") {
        if (cellObject.value) style.backColor = "#DDD8C8";
      }
      if (cellType === "cell-output") {
        style.backColor = "#E4EFF4";
        isBold = true;
      }
      if (["cell-title-input", "cell-title-output"].includes(cellType)) {
        style.foreColor = "#363636";
      }
      if (cellType === "cell-description") {
        style.foreColor = "#BFBFBF";
      }
      if (cellType === "table-header") {
        style.foreColor = "#000000";
        style.backColor = "#D4D4D4";
        isBold = true;
      }
      if (formatter || formatter === "0" || formatter === 0)
        style.formatter = formatter;

      cell.setStyle(style, GC.Spread.Sheets.SheetArea.viewport);
      if (isBold) cell.fontWeight("bold");
    }
  }

  //=========================================
  // WORKBOOK TABLES
  //=========================================

  async addTableSheet(spread, tableItem, options) {
    if (!spread || !tableItem) return;
    const tableName = tableItem.data?.worksheet ?? "Table"; //removeSpecialCharacters(tableItem?.title) ?? "Table"; //?.replaceAll(" ", "") ?? "Table";
    // const itemSchema = tableItem?.data?.schema;
    const tableId = tableItem?.id; // + "_domaintable";
    const isRecords = tableItem?.sourceType === "records";
    const isHostedTable = tableItem?.sourceType === "table";
    const isJson = tableItem?.sourceType === "json";
    const isAutomation = tableItem?.sourceType === "automation";
    const token = tableItem?.data?.auth?.token;
    // const isWebservice = tableItem?.sourceType === "webservice";
    const basicApiUrl = this.rootStore.tables.basicApiUrl;
    let dataUrl;
    let newTable = null;
    let allowAddNew = false;
    let columns;

    this.setIsWorkingElement(tableItem.id);
    try {
      spread.suspendPaint();
      spread.suspendCalcService();

      // init a data manager
      const dataManager = spread.dataManager();

      //==========================================
      // JSON source
      //==========================================
      if (isJson) {
        dataUrl = tableItem.data?.sourceUrl;
        newTable = dataManager.addTable(tableId, {
          remote: {
            read: {
              url: dataUrl, //"https://demodata.mescius.io/northwind/api/v1/Orders",
            },
          },
          batch: true,
          onFailed: function (type, args) {
            console.log("ERROR addTableSheet", type, args);
          },
        });
      }
      //==========================================
      // RECORDS source
      //==========================================
      if (isRecords || isAutomation) {
        dataUrl = basicApiUrl + "tablerecords?id=" + tableItem.id;
        newTable = dataManager.addTable(tableId, {
          remote: {
            read: {
              url: dataUrl,
              method: "GET",
              dataType: "json",
            },
          },
          batch: true,
          onFailed: function (type, args) {
            console.log("ERROR addTableSheet", type, args);
          },
        });
      }
      //==========================================
      // HOSTED TABLE source
      //==========================================
      if (isHostedTable) {
        dataUrl = basicApiUrl + "table?id=" + tableItem.id;
        if (token) dataUrl += "&token=" + token;
        // columns = itemSchema?.columns ?? [];
        newTable = dataManager.addTable(tableId, {
          remote: {
            read: {
              url: dataUrl,
              method: "GET",
              dataType: "json",
            },
            // read: function () {
            //   return sendHostedTableRequest(
            //     "read",
            //     { method: "GET" },
            //     tableItem
            //   );
            // },
            batch: function (changes) {
              return sendHostedTableRequest(
                "batch",
                { body: changes },
                tableItem
              );
            },
          },
          batch: true,
          // schema: {
          //   columns: columns,
          // },
        });
        allowAddNew = true;
      }
      //==========================================
      // get the data and bind the table
      //==========================================
      if (newTable) {
        try {
          await newTable.fetch();
        } catch (error) {
          console.log("ERROR addTableSheet newTable.fetch", error);
        }
        const newView = newTable.addView(tableName, columns);
        // optimize column widths
        let columnInfos = newView?.getColumnInfos() ?? [];
        if (columnInfos) {
          for (const [columnIndex, columnObject] of Object.entries(
            columnInfos
          )) {
            const width = columnObject?.width;
            if (width < 80) columnObject.width = 150;
          }
        }
        // create the tab
        const tabIndex =
          spread.getSheetCount() + spread.getSheetTabCount() ?? 0;
        const newSheetTab = spread.addSheetTab(
          tabIndex,
          "", //tableName,
          GC.Spread.Sheets.SheetType.tableSheet
        );
        newSheetTab.options.sheetTabColor = "#DDEBF8";
        let newName = newSheetTab.tableName();
        newSheetTab.options.allowAddNew = allowAddNew;
        newSheetTab.setDataView(newView);
        newName && spread.setActiveSheetTab(newName);
        this.setActiveFileModified(true);
      }

      //==========================================
      // add the table info to the file info
      //==========================================
      if (options?.addTableInfos) {
        try {
          // let sheetCount = spread.sheets?.length;
          // let tableCount = spread.getSheetTabCount();
          const sheetTab = spread.getSheetTab(0);
          const sheet = spread.sheets[0];
          spread.setActiveSheet(sheet.name());
          const tableSheetName = sheetTab.name();
          if (sheet) {
            sheet.getCell(0, 0).value("Table Name");
            sheet.getCell(0, 1).value(tableItem.title);
            // sheet.getCell(1, 0).value("Table ID");
            // sheet.getCell(1, 1).value(tableItem.id);
            sheet.getCell(1, 0).value("Rows Count");
            sheet.getCell(1, 1).formula(`=ROWS(${tableSheetName})`);

            sheet.autoFitColumn(0);
            sheet.autoFitColumn(1);
          }
        } catch (error) {
          console.log("ERROR addTableInfos", error);
        }
      }
    } catch (error) {
      console.log("ERROR addTableSheet", error);
      return;
    } finally {
      spread.resumeCalcService();
      spread.resumePaint();
      this.setIsWorkingElement(null);
    }
  }

  async downloadHostedFileByUrl(fileUrl) {
    if (!fileUrl) return;

    try {
      let data = await axios({
        url: fileUrl,
        method: "GET",
        responseType: "blob", // important
      });
      return data?.data;
    } catch (err) {
      console.log("ERROR axios downloadHostedFileByUrl", err);
    }
  }

  // async downloadHostedFile(hostedFileId, returnData = false) {
  //   let domainId = this.rootStore.main.userDomain;
  //   let versionId;
  //   if (!hostedFileId || !domainId) return;

  //   versionId = "-1";

  //   // get the secure download url
  //   let downloadUrl = await this.getHostedFileDownloadUrl(
  //     domainId,
  //     hostedFileId,
  //     versionId
  //   );
  //   if (!downloadUrl) return false;

  //   // download the data and store compressed in sessionStorage
  //   try {
  //     let data = await axios(downloadUrl, {
  //       // query URL without using browser cache
  //       headers: {
  //         "Cache-Control": "no-cache",
  //         Pragma: "no-cache",
  //         Expires: "0",
  //       },
  //     });
  //     let compressedData = data?.data;
  //     return compressedData;
  //   } catch (err) {
  //     console.log("ERROR axios isLocalDataAvailable", err);
  //   }
  // }

  // async deleteHostedFile(hostedFileId, cacheVersion) {
  //   try {
  //     let domainId = this.rootStore.main.userDomain;
  //     let versionId = "-1";

  //     // delete the old file from the cache
  //     await this.rootStore.appsync.deleteHostedFile(
  //       domainId,
  //       hostedFileId,
  //       versionId
  //     );
  //     return true;
  //   } catch (err) {
  //     console.log("ERROR deleteHostedFile", err);
  //     return false;
  //   }
  // }
}

// async getFileFromHostedFileId(fileId) {
//   let fileUrl = null;
//   let blobFile = {};
//   let file = null;
//   let fileName = null;

//   this.setActiveFileId(fileId);

//   // it is a new file
//   // if (fileId === this.activeNewHostedFileId) {
//   //   // get an empty file object
//   //   fileUrl =
//   //     "https://airrange-content.s3.eu-central-1.amazonaws.com/templates/hostedfile-new.xlsx";
//   //   fileName = "New.xlsx";
//   //   this.activeFileModified = true;
//   //   // create a file object
//   //   blobFile = await this.downloadHostedFileByUrl(fileUrl);
//   //   file = new File([blobFile], fileName, { type: blobFile.type });
//   //   return file;
//   // }

//   // it is not a new file
//   if (fileId !== this.activeNewHostedFileId) {
//     let fileData = await this.rootStore.appsync.getHostedFile(fileId);
//     if (fileData?.data) {
//       let spreadJson = decompressJson(fileData?.data);
//     }
//     let fileInfo = {
//       fileId: fileData?.fileId,
//       fileName: fileData?.fileName,
//       fileType: fileData?.fileType,
//       topImageUrl: fileData?.topImageUrl,
//       collaboratorEmails: fileData?.collaboratorEmails,
//     }
//     this.setActiveFileInfo(fileInfo);
//   }
//   this.activeNewHostedFileId = null;
//   this.activeFileModified = false;

//   // if (!fileUrl) {
//   //   // get an empty file object
//   //   fileUrl =
//   //     "https://airrange-content.s3.eu-central-1.amazonaws.com/templates/hostedfile-new.xlsx";
//   //   fileName = "New.xlsx";
//   //   this.activeFileModified = true;
//   // }
//   // // create a file object
//   // blobFile = await this.downloadHostedFileByUrl(fileUrl);
//   // file = new File([blobFile], fileName, { type: blobFile.type });
//   // return file;
// }

// async getHostedFileUploadUrl(domainId, hostedFileId, versionId) {
//   // use our own cache service
//   const uploadUrl = await this.rootStore.appsync.getHostedFileUploadUrl(
//     domainId,
//     hostedFileId,
//     versionId
//   );
//   return uploadUrl;
// }

// async uploadHostedFile(hostedFileId, jsonData, callback) {
//   try {
//     let domainId = this.rootStore.main.userDomain;
//     let versionId = "-1";

//     let saveData = compressJson(jsonData);
//     let objectSize = getObjectSize(saveData);
//     console.log("INFO hostedFileSize", objectSize);

//     // delete the old file from the cache
//     // await this.rootStore.appsync.deleteCacheData(
//     //   workbookId,
//     //   elementId,
//     //   versionId
//     // );

//     // get a time limited upload url and upload it
//     const uploadUrl = await this.getHostedFileUploadUrl(
//       domainId,
//       hostedFileId,
//       versionId
//     );
//     if (!uploadUrl) {
//       console.log("ERROR uploadHostedFile - no uploadUrl");
//       return;
//     }

//     await axios.put(uploadUrl, saveData);
//     // inform ready
//     if (callback) callback(uploadUrl);

//     return true;
//   } catch (err) {
//     console.log("ERROR uploadHostedFile", err);
//     return false;
//   }
// }

// async getHostedFileDownloadUrl(domainId, hostedFileId, versionId) {
//   // check if the tenant has its own download storage

//   // get from our aws S3 bucket
//   let downloadUrl = await this.rootStore.appsync.getHostedFileDownloadUrl(
//     domainId,
//     hostedFileId,
//     versionId
//   );
//   return downloadUrl;
// }
